import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import type { FunctionReturnType } from 'convex/server'
import { Plus, Trash2 } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatCurrency } from '@/lib/utils'
import { calculateExternalLineTotalCents } from '../../../shared/externalWorkCosts'
import { defaultExternalPrescriber, hydrateExternalDraftCosts } from '@/lib/externalPrescriptionDraft'

export type ExternalPrescriptionRecord = NonNullable<FunctionReturnType<typeof api.externalLabs.getPrescription>>

type EditableLine = {
  key: string
  line_id?: string
  price_item_id?: Id<'external_price_items'>
  description: string
  quantity: string
  unit_cost: string | null
}

const fieldClass = 'w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
const labelClass = 'mb-1.5 block text-sm font-medium text-gray-800'

export function externalPrescriptionError(error: unknown, fallback: string): string {
  return error instanceof ConvexError && typeof error.data === 'string' ? error.data : fallback
}

export function ExternalPrescriptionForm({ laboratoryId, prescription, unavailableReason, onSuccess, onCancel, onSavingChange }: {
  laboratoryId: Id<'external_laboratories'>
  prescription?: ExternalPrescriptionRecord
  unavailableReason?: string
  onSuccess: (id: Id<'external_prescriptions'>) => void
  onCancel: () => void
  onSavingChange?: (saving: boolean) => void
}) {
  const prefix = useId()
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const canViewPrices = hasPermission('view_external_prices')
  const createPrescription = useMutation(api.externalLabs.createPrescription)
  const updatePrescription = useMutation(api.externalLabs.updatePrescription)
  const priceItems = useQuery(api.externalLabs.listPriceItems, !unavailableReason && profile?.company_id && hasPermission('create_external_prescription') ? {
    laboratory_id: laboratoryId,
    prescription_id: prescription?._id,
  } : 'skip')
  const activeLaboratories = useQuery(api.externalLabs.listLaboratories, prescription && profile?.company_id && hasPermission('create_external_prescription') ? {} : 'skip')
  const historicalLaboratoryUnavailable = Boolean(prescription && activeLaboratories !== undefined && !activeLaboratories.some((lab) => lab._id === laboratoryId))
  const canAddLines = !unavailableReason && (!prescription || (activeLaboratories !== undefined && !historicalLaboratoryUnavailable))
  const [patientName, setPatientName] = useState(prescription?.patient_name ?? '')
  const [doctorName, setDoctorName] = useState(prescription?.doctor_name ?? defaultExternalPrescriber(profile))
  const [description, setDescription] = useState(prescription?.work_description ?? '')
  const [testPhase, setTestPhase] = useState(prescription?.test_phase ?? '')
  const [notes, setNotes] = useState(prescription?.notes ?? '')
  const [dueDate, setDueDate] = useState(prescription?.due_date ?? '')
  const [lines, setLines] = useState<EditableLine[]>(() => prescription?.lines.map((line, index) => ({
    key: `existing-${index}`,
    line_id: line.line_id,
    price_item_id: line.price_item_id,
    description: line.description,
    quantity: String(line.quantity),
    unit_cost: line.unit_cost === undefined ? null : String(line.unit_cost),
  })) ?? [])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [selectedPriceItem, setSelectedPriceItem] = useState('')
  const mounted = useRef(true)
  const savingRef = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      onSavingChange?.(false)
    }
  }, [onSavingChange])

  useEffect(() => {
    if (canViewPrices) setLines((current) => hydrateExternalDraftCosts(current, prescription?.lines, priceItems))
  }, [canViewPrices, prescription?.lines, priceItems])

  const updateLine = (key: string, values: Partial<EditableLine>) => {
    setLines((current) => current.map((line) => line.key === key ? { ...line, ...values } : line))
  }
  const addLine = () => {
    if (!canAddLines) return
    const item = priceItems?.find((candidate) => candidate._id === selectedPriceItem)
    setLines((current) => [...current, {
      key: crypto.randomUUID(),
      price_item_id: item?._id,
      description: item?.name ?? '',
      quantity: '1',
      unit_cost: item ? (canViewPrices && item.unit_cost !== undefined ? String(item.unit_cost) : null) : '0',
    }])
    setSelectedPriceItem('')
  }

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (savingRef.current || unavailableReason || !profile?.company_id) return
    setError(null)
    if (!patientName.trim() || !doctorName.trim() || !description.trim()) {
      setError('Inserisci il paziente, il medico e la descrizione del lavoro.')
      return
    }
    if (lines.some((line) => !line.description.trim() || !Number.isFinite(Number(line.quantity)) || Number(line.quantity) <= 0 || Number(line.quantity) > 1000 || (canViewPrices && line.unit_cost !== null && (line.unit_cost === '' || !Number.isFinite(Number(line.unit_cost)) || Number(line.unit_cost) < 0)))) {
      setError('Controlla le lavorazioni: descrizione obbligatoria, quantità maggiore di zero e fino a 1.000, costo non negativo.')
      return
    }
    savingRef.current = true
    setSaving(true)
    onSavingChange?.(true)
    const values = {
      patient_name: patientName.trim(),
      doctor_name: doctorName.trim(),
      work_description: description.trim(),
      test_phase: testPhase.trim() || undefined,
      notes: notes.trim() || undefined,
      due_date: dueDate || undefined,
      lines: lines.map((line) => ({
        line_id: line.line_id,
        price_item_id: line.price_item_id,
        description: line.description.trim(),
        quantity: Number(line.quantity),
        ...(canViewPrices && line.unit_cost !== null && line.unit_cost !== '' ? { unit_cost: Number(line.unit_cost) } : {}),
      })),
    }
    try {
      if (prescription) {
        await updatePrescription({ id: prescription._id, ...values })
        if (mounted.current) onSuccess(prescription._id)
      } else {
        const id = await createPrescription({ laboratory_id: laboratoryId, expected_company_id: profile.company_id as Id<'companies'>, ...values })
        if (mounted.current) onSuccess(id)
      }
    } catch (cause) {
      if (mounted.current) setError(externalPrescriptionError(cause, 'Salvataggio non riuscito. I dati inseriti sono ancora qui: riprova.'))
    } finally {
      savingRef.current = false
      if (mounted.current) {
        setSaving(false)
        onSavingChange?.(false)
      }
    }
  }

  const total = lines.reduce((sum, line) => sum + calculateExternalLineTotalCents(
    Math.round(((Number(line.unit_cost) || 0) + Number.EPSILON) * 100),
    Number(line.quantity) || 0,
  ), 0) / 100
  const hasUnknownCosts = lines.some((line) => line.unit_cost === null)

  return (
    <form onSubmit={save} className="space-y-7" aria-busy={saving}>
      {unavailableReason && <p role="status" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">{unavailableReason}</p>}
      <fieldset disabled={saving} className="min-w-0 space-y-7">
        <section aria-labelledby={`${prefix}-patient-title`}>
          <h2 id={`${prefix}-patient-title`} className="text-lg font-semibold text-gray-900">Paziente e medico</h2>
          <p className="mt-1 text-sm text-gray-600">I campi contrassegnati con * sono obbligatori.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor={`${prefix}-patient`}>Paziente *</label>
              <Input id={`${prefix}-patient`} value={patientName} onChange={(event) => setPatientName(event.target.value)} required maxLength={200} autoComplete="off" />
            </div>
            <div>
              <label className={labelClass} htmlFor={`${prefix}-doctor`}>Medico prescrittore *</label>
              <Input id={`${prefix}-doctor`} value={doctorName} onChange={(event) => setDoctorName(event.target.value)} required maxLength={200} autoComplete="off" />
            </div>
          </div>
        </section>

        <section className="border-t pt-6" aria-labelledby={`${prefix}-work-title`}>
          <h2 id={`${prefix}-work-title`} className="text-lg font-semibold text-gray-900">Indicazioni per il laboratorio</h2>
          <div className="mt-4 space-y-4">
            <div>
              <label className={labelClass} htmlFor={`${prefix}-description`}>Lavoro richiesto *</label>
              <textarea id={`${prefix}-description`} className={`${fieldClass} min-h-32`} value={description} onChange={(event) => setDescription(event.target.value)} required maxLength={4000} rows={4} placeholder="Descrivi il lavoro, gli elementi dentali, i materiali e il colore." />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor={`${prefix}-phase`}>Prova o fase</label>
                <Input id={`${prefix}-phase`} value={testPhase} onChange={(event) => setTestPhase(event.target.value)} maxLength={300} placeholder="Scrivi liberamente la prova richiesta" />
              </div>
              <div>
                <label className={labelClass} htmlFor={`${prefix}-due-date`}>Data di consegna richiesta</label>
                <Input id={`${prefix}-due-date`} type="date" min="1900-01-01" max="2200-12-31" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelClass} htmlFor={`${prefix}-notes`}>Note per il laboratorio</label>
              <textarea id={`${prefix}-notes`} className={fieldClass} value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={6000} rows={3} aria-describedby={`${prefix}-notes-hint`} />
              <p id={`${prefix}-notes-hint`} className="mt-1 text-sm text-gray-600">Le note saranno incluse nel PDF della prescrizione.</p>
            </div>
          </div>
        </section>

        <section className="border-t pt-6" aria-labelledby={`${prefix}-lines-title`}>
          <h2 id={`${prefix}-lines-title`} className="text-lg font-semibold text-gray-900">Lavorazioni</h2>
          <p className="mt-1 text-sm text-gray-600">Facoltative. Aggiungi una voce del listino del laboratorio oppure una lavorazione libera.</p>
          {historicalLaboratoryUnavailable && <p role="status" className="mt-2 text-sm text-amber-900">Il laboratorio non è più disponibile per questa clinica. Puoi aggiornare il lavoro e le righe già presenti; non puoi aggiungere nuove lavorazioni.</p>}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <label className={labelClass} htmlFor={`${prefix}-price-item`}>Voce da aggiungere</label>
              <select id={`${prefix}-price-item`} disabled={!canAddLines} className={`${fieldClass} h-10`} value={selectedPriceItem} onChange={(event) => setSelectedPriceItem(event.target.value)}>
                <option value="">Lavorazione libera</option>
                {priceItems?.map((item) => <option key={item._id} value={item._id}>{item.name}{canViewPrices && item.unit_cost !== undefined ? ` · ${formatCurrency(item.unit_cost)}` : ''}</option>)}
              </select>
            </div>
            <Button type="button" variant="outline" onClick={addLine} disabled={!canAddLines || lines.length >= 100}><Plus className="mr-2 h-4 w-4" aria-hidden="true" />Aggiungi lavorazione</Button>
          </div>
          {lines.length > 0 && <div className="mt-5 divide-y border-y">
            {lines.map((line, index) => <div key={line.key} className={`grid min-w-0 items-start gap-3 py-4 ${canViewPrices ? 'sm:grid-cols-[minmax(0,1fr)_6rem_8rem_auto]' : 'sm:grid-cols-[minmax(0,1fr)_6rem_auto]'}`}>
              <div>
                <label className={labelClass} htmlFor={`${prefix}-line-${line.key}`}>Lavorazione {index + 1}</label>
                <Input id={`${prefix}-line-${line.key}`} value={line.description} onChange={(event) => updateLine(line.key, { description: event.target.value })} required maxLength={2000} />
              </div>
              <div>
                <label className={labelClass} htmlFor={`${prefix}-qty-${line.key}`}>Quantità</label>
                <Input id={`${prefix}-qty-${line.key}`} type="number" min="0.001" max="1000" step="0.001" value={line.quantity} onChange={(event) => updateLine(line.key, { quantity: event.target.value })} required className="tabular-nums" />
              </div>
              {canViewPrices && <div>
                <label className={labelClass} htmlFor={`${prefix}-cost-${line.key}`}>Costo unit. (€)</label>
                <Input id={`${prefix}-cost-${line.key}`} type="number" min="0" max="1000000" step="0.01" value={line.unit_cost ?? ''} onChange={(event) => updateLine(line.key, { unit_cost: event.target.value })} className="tabular-nums read-only:bg-gray-50" readOnly={Boolean(line.price_item_id) || line.unit_cost === null} required={line.unit_cost !== null} aria-describedby={line.price_item_id || line.unit_cost === null ? `${prefix}-cost-hint-${line.key}` : undefined} />
                {(line.price_item_id || line.unit_cost === null) && <p id={`${prefix}-cost-hint-${line.key}`} className="mt-1 text-xs text-gray-600">{line.unit_cost === null ? 'Caricamento costo…' : 'Costo dal listino'}</p>}
              </div>}
              <Button type="button" variant="ghost" className="justify-self-start sm:mt-6" onClick={() => setLines((current) => current.filter((candidate) => candidate.key !== line.key))} aria-label={`Rimuovi lavorazione ${index + 1}`}><Trash2 className="h-4 w-4" aria-hidden="true" /><span className="ml-2 sm:sr-only">Rimuovi</span></Button>
            </div>)}
          </div>}
          {canViewPrices && lines.length > 0 && (hasUnknownCosts ? <p role="status" className="mt-3 text-right text-sm text-gray-600">Caricamento costi…</p> : <p className="mt-3 text-right text-sm text-gray-700">Totale: <strong className="tabular-nums text-gray-900">{formatCurrency(total)}</strong></p>)}
        </section>
      </fieldset>

      {error && <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      <div className="flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>Annulla</Button>
        <Button type="submit" variant="primoup" className="text-gray-950" disabled={saving || Boolean(unavailableReason)}>{saving ? 'Salvataggio…' : prescription ? 'Salva modifiche' : 'Salva prescrizione'}</Button>
      </div>
    </form>
  )
}
